import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useSesion } from '../sesion';

/**
 * Todo lo administrable en un solo lugar.
 *
 * Cada subsección conserva su permiso, que no es el mismo: Almacén
 * administra refacciones, Compras administra proveedores, y los maestros
 * y las personas quedan para administración y gerencia. Agrupar en el
 * menú no significa abrir el acceso.
 */

export interface Subseccion {
  ruta: string;
  etiqueta: string;
  modulo: string;
  descripcion: string;
}

export const SUBSECCIONES: Subseccion[] = [
  {
    ruta: 'catalogos',
    etiqueta: 'Catálogos',
    modulo: 'catalogos',
    descripcion: 'Regiones, estados, áreas, equipos, marcas, modelos, igualas,'
      + ' bancos y los conceptos con que se arma una factura.',
  },
  {
    ruta: 'refacciones',
    etiqueta: 'Refacciones',
    modulo: 'refacciones',
    descripcion: 'Catálogo de piezas y los equipos donde sirve cada una.',
  },
  {
    ruta: 'proveedores',
    etiqueta: 'Proveedores',
    modulo: 'proveedores',
    descripcion: 'Padrón de proveedores y sus datos de pago.',
  },
  {
    ruta: 'tarifas-iguala',
    etiqueta: 'Tarifas de iguala',
    modulo: 'catalogos',
    descripcion: 'El precio pactado de la iguala, por tipo de servicio y formato de tienda.',
  },
  {
    ruta: 'precios-preventivos',
    etiqueta: 'Precios del preventivo',
    modulo: 'catalogos',
    descripcion: 'Lo que se cobra por cada servicio preventivo, y las sucursales que cobran distinto.',
  },
  {
    ruta: 'cobertura',
    etiqueta: 'Cobertura de la iguala',
    modulo: 'cobertura',
    descripcion: 'Qué trabajo absorbe la iguala y cuál se le cobra, por área y formato de tienda.',
  },
  {
    ruta: 'usuarios',
    etiqueta: 'Usuarios',
    modulo: 'usuarios',
    descripcion: 'Quién entra al sistema y qué puede hacer.',
  },
  {
    ruta: 'roles',
    etiqueta: 'Roles y permisos',
    modulo: 'usuarios',
    descripcion: 'Qué puede hacer cada rol en cada módulo.',
  },
  {
    ruta: 'tecnicos',
    etiqueta: 'Técnicos',
    modulo: 'usuarios',
    descripcion: 'A quién se asigna una orden y a quién se le paga.',
  },
];

/** Las que este perfil puede al menos consultar. */
/**
 * Las subsecciones que este perfil puede administrar.
 *
 * Se filtra por `crear` o `editar`, no por `ver`. Casi todos los roles
 * tienen `catalogos:ver` y `refacciones:ver` porque es lo que llena los
 * desplegables al capturar una orden y lo que permite elegir una pieza al
 * pedirla —consultar, no administrar—. Filtrando por `ver`, un supervisor
 * de zona acababa con la sección de Administración en su menú por el sólo
 * hecho de poder leer el catálogo de marcas.
 *
 * Aquí el criterio es el que corresponde a la sección: se ofrece
 * Administración a quien administra algo.
 */
export function subseccionesVisibles(puede: (m: string, a: string) => boolean): Subseccion[] {
  return SUBSECCIONES.filter((s) => puede(s.modulo, 'crear') || puede(s.modulo, 'editar'));
}

export default function Administracion() {
  const { puede } = useSesion();
  const ubicacion = useLocation();

  const visibles = subseccionesVisibles(puede);

  if (visibles.length === 0) {
    return (
      <div className="panel">
        <h1>Administración</h1>
        <p className="sub">
          Tu perfil no tiene acceso a ninguna sección de administración.
        </p>
      </div>
    );
  }

  // Entrar a /administracion a secas lleva a la primera que sí pueda ver.
  if (ubicacion.pathname.replace(/\/$/, '').endsWith('/administracion')) {
    return <Navigate to={visibles[0].ruta} replace />;
  }

  return (
    <>
      <nav className="submenu">
        {visibles.map((s) => (
          <NavLink
            key={s.ruta}
            to={s.ruta}
            className={({ isActive }) => (isActive ? 'activo' : '')}
          >
            {s.etiqueta}
          </NavLink>
        ))}
      </nav>

      <Outlet />
    </>
  );
}
